import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../interceptors";

const EMPTY_PAGE = {
  slug: "",
  title: "",
  heroHeadline: "",
  heroSubheading: "",
  heroImageUrl: "",
  primaryColor: "#7B5AC4",
  logoUrl: "",
  aboutHtml: "",
  footerText: "",
  showAllJobs: true,
  featuredJobIds: "[]",
  metaTitle: "",
  metaDescription: "",
};

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,78}[a-z0-9]$/;
const COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export default function CareerPageEditor({ mode }) {
  const nav = useNavigate();
  const { id } = useParams();
  const isNew = mode === "new";

  const [form, setForm] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/job-posting/career-pages/${id}`);
        if (cancelled) return;
        setForm({ ...EMPTY_PAGE, ...data });
      } catch (e) {
        setError(e?.message || "Failed to load page");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validationError = useMemo(() => {
    if (!form.title.trim()) return "Title is required";
    if (!form.slug.trim()) return "Slug is required";
    if (!SLUG_RE.test(form.slug)) return "Slug must be 3–80 chars, lowercase a–z, 0–9, hyphen; start/end with alphanumeric";
    if (form.primaryColor && !COLOR_RE.test(form.primaryColor)) return "Primary color must be #RRGGBB";
    if (form.heroImageUrl && !form.heroImageUrl.startsWith("https://")) return "Hero image URL must use https://";
    if (form.logoUrl && !form.logoUrl.startsWith("https://")) return "Logo URL must use https://";
    return null;
  }, [form]);

  const save = async (alsoPublish = false) => {
    if (validationError) { setError(validationError); return; }
    setSaving(true);
    setError(null);
    try {
      const url = isNew ? "/job-posting/career-pages" : `/job-posting/career-pages/${id}`;
      const method = isNew ? "post" : "put";
      const { data } = await axiosInstance[method](url, form);
      if (alsoPublish && data?.id) {
        await axiosInstance.post(`/job-posting/career-pages/${data.id}/publish`);
      }
      setToast(alsoPublish ? "Saved and published" : "Saved");
      setTimeout(() => setToast(null), 1500);
      // After successful create, redirect to edit view so subsequent saves use PUT.
      if (isNew && data?.id) nav(`/admin/career-pages/${data.id}`, { replace: true });
    } catch (e) {
      setError(e?.data?.error || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-sm text-vf-muted">Loading…</div>;
  }

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">
            {isNew ? "New career page" : `Edit · ${form.title || "Untitled"}`}
          </h1>
          <p className="text-sm text-vf-muted mt-1">
            Public URL: <code className="text-xs">/careers/{form.slug || "your-slug"}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav("/admin/career-pages")} className="px-3 py-1.5 rounded border border-slate-300 text-xs">
            ← Back
          </button>
          <button
            onClick={() => save(false)}
            disabled={saving}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs disabled:opacity-50"
          >
            {saving ? "…" : "Save draft"}
          </button>
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
          >
            {saving ? "…" : "Save and publish"}
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700 mb-4">{error}</div>
      )}
      {toast && (
        <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-700 mb-4">{toast}</div>
      )}

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-8 space-y-4">
          <Section title="Identity">
            <Row>
              <Field label="Slug" required>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => update("slug", e.target.value.toLowerCase())}
                  placeholder="main"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono"
                />
              </Field>
              <Field label="Title" required>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  placeholder="Acme Careers"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </Field>
            </Row>
          </Section>

          <Section title="Hero">
            <Field label="Headline">
              <input
                type="text"
                value={form.heroHeadline || ""}
                onChange={(e) => update("heroHeadline", e.target.value)}
                maxLength={200}
                placeholder="Build the future of hiring with us"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
              <CharCount value={form.heroHeadline} max={200} />
            </Field>
            <Field label="Subheading">
              <textarea
                value={form.heroSubheading || ""}
                onChange={(e) => update("heroSubheading", e.target.value)}
                rows={3}
                placeholder="A short line about the team and the mission."
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
            </Field>
            <Field label="Hero image URL">
              <input
                type="text"
                value={form.heroImageUrl || ""}
                onChange={(e) => update("heroImageUrl", e.target.value)}
                placeholder="https://… (1600×600 recommended)"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
            </Field>
          </Section>

          <Section title="Branding">
            <Row>
              <Field label="Logo URL">
                <input
                  type="text"
                  value={form.logoUrl || ""}
                  onChange={(e) => update("logoUrl", e.target.value)}
                  placeholder="https://… (200×60)"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </Field>
              <Field label="Primary color">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={form.primaryColor || "#7B5AC4"}
                    onChange={(e) => update("primaryColor", e.target.value)}
                    className="w-10 h-9 border border-slate-300 rounded"
                  />
                  <input
                    type="text"
                    value={form.primaryColor || ""}
                    onChange={(e) => update("primaryColor", e.target.value)}
                    placeholder="#7B5AC4"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm font-mono"
                  />
                </div>
              </Field>
            </Row>
          </Section>

          <Section title="About">
            <Field label="About (HTML)">
              <textarea
                value={form.aboutHtml || ""}
                onChange={(e) => update("aboutHtml", e.target.value)}
                rows={6}
                placeholder="<p>Tell candidates what makes your company a great place to work.</p>"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono"
              />
              <div className="text-[10px] text-vf-muted mt-1">
                HTML is sanitized server-side before being served on the public page.
                Rich-text editor coming in a follow-up.
              </div>
            </Field>
          </Section>

          <Section title="Jobs">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={!!form.showAllJobs}
                onChange={(e) => update("showAllJobs", e.target.checked)}
              />
              Show all open jobs
            </label>
            {!form.showAllJobs && (
              <Field label="Featured job IDs (JSON array)">
                <input
                  type="text"
                  value={form.featuredJobIds || "[]"}
                  onChange={(e) => update("featuredJobIds", e.target.value)}
                  placeholder="[10, 12, 14]"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono"
                />
              </Field>
            )}
          </Section>

          <Section title="Footer">
            <Field label="Footer text">
              <input
                type="text"
                value={form.footerText || ""}
                onChange={(e) => update("footerText", e.target.value)}
                placeholder="© Acme Inc. — careers"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
            </Field>
          </Section>

          <Section title="SEO">
            <Field label="Meta title">
              <input
                type="text"
                value={form.metaTitle || ""}
                onChange={(e) => update("metaTitle", e.target.value)}
                maxLength={160}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
              <CharCount value={form.metaTitle} max={160} />
            </Field>
            <Field label="Meta description">
              <textarea
                value={form.metaDescription || ""}
                onChange={(e) => update("metaDescription", e.target.value)}
                rows={2}
                maxLength={300}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
              <CharCount value={form.metaDescription} max={300} />
            </Field>
          </Section>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="sticky top-4 bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-3">Mini preview</div>
            <div
              className="rounded-lg p-4 text-white mb-3"
              style={{ background: COLOR_RE.test(form.primaryColor || "") ? form.primaryColor : "#7B5AC4" }}
            >
              <div className="font-bold text-base mb-1">{form.heroHeadline || form.title || "Headline"}</div>
              <div className="text-xs opacity-90 line-clamp-3">{form.heroSubheading || "Subheading appears here"}</div>
            </div>
            <div className="text-[11px] text-vf-muted">
              Full live preview is on the follow-up list. For now, click <b>View ↗</b>{" "}
              from the list page after saving + publishing.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs text-vf-muted mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

function CharCount({ value, max }) {
  const len = (value || "").length;
  return <div className="text-[10px] text-vf-muted mt-0.5">{len} / {max}</div>;
}
